import isEmpty from 'lodash.isempty';
import { FC } from 'react';

import {
  AssessmentSchemaType,
  AssessmentType,
  CompletedProgramAssessmentsDataQuery,
} from '../../../generated/graphql';
import { getTimeStamp } from '../../lib/time';
import ChevronRight from '../../svgs/ChevronRight';
import SlideoverSection from '../SlideoverSection';
import InlineScore from './InlineScore';
import { getScoreProps } from './helpers';

type CompletedProgramAssessments =
  CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'];

const HistorySection: FC<{
  completedProgramAssessments?: CompletedProgramAssessments;
  assessmentType?: AssessmentType;
  assessmentSchemaType?: AssessmentSchemaType;
  setShownAssessmentQandAIndex: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}> = ({
  completedProgramAssessments,
  assessmentType,
  setShownAssessmentQandAIndex,
}) => {
  return (
    <SlideoverSection title="History" className="px-0" titleClassName="px-6">
      <div className="flex w-full flex-col items-center justify-start divide-y divide-neutral-100/25">
        {completedProgramAssessments?.map(
          (completedProgramAssessment, completedProgramAssessmentIndex) => {
            const { primary, secondary, tertiary } = getScoreProps(
              completedProgramAssessment,
              assessmentType,
            );

            const isPractitionerForm =
              completedProgramAssessment.assessment
                ?.completableByProviderOnly === true;
            const signedSignatureUser =
              completedProgramAssessment.programAssessmentSignatureUsers?.find(
                (u) => u.signedAt,
              );
            const completedByProviderName =
              signedSignatureUser?.providerUser?.name ??
              (completedProgramAssessment.completedByType === 'provider'
                ? completedProgramAssessment.completedByProviderUser?.name
                : null);
            const completedByExternalName =
              completedProgramAssessment.completedByType === 'external_party'
                ? completedProgramAssessment.completedByExternalName ??
                  'External'
                : null;
            const completedByClient =
              isPractitionerForm &&
              completedProgramAssessment.completedByType === 'patient';
            const completedByLabel = isPractitionerForm
              ? completedByProviderName
                ? `Completed by ${completedByProviderName}`
                : completedByExternalName
                ? `Completed by ${completedByExternalName}`
                : completedByClient
                ? 'Completed by client'
                : null
              : null;

            return (
              <button
                key={completedProgramAssessmentIndex}
                onClick={() =>
                  setShownAssessmentQandAIndex(completedProgramAssessmentIndex)
                }
                className="focus:ring-green-5 flex w-full flex-row items-center justify-between px-6 py-3 hover:bg-neutral-25 focus:rounded-lg focus:outline-none focus:ring-2"
              >
                <div className="flex flex-col items-start">
                  {!isPractitionerForm && (
                    <div className="text-small-caption text-green-150">
                      Sent{' '}
                      {getTimeStamp(
                        completedProgramAssessment.startDate,
                        false,
                      )}
                    </div>
                  )}
                  {!isEmpty(primary) && (
                    <div className="my-1.5 w-full">
                      <div className="flex flex-col items-start justify-start">
                        <InlineScore
                          {...primary}
                          categorization={
                            primary.shortLabel ?? primary.categorization
                          }
                        />
                        {!isEmpty(secondary) && (
                          <InlineScore
                            {...secondary}
                            categorization={
                              secondary.shortLabel ?? secondary.categorization
                            }
                          />
                        )}
                        {!isEmpty(tertiary) && (
                          <InlineScore
                            {...tertiary}
                            categorization={
                              tertiary.shortLabel ?? tertiary.categorization
                            }
                          />
                        )}
                      </div>
                    </div>
                  )}
                  <div className="text-small-caption font-medium text-neutral-125">
                    Completed{' '}
                    {getTimeStamp(
                      completedProgramAssessment.completedAt,
                      false,
                      true,
                    )}
                  </div>
                  {completedByLabel && (
                    <div className="mt-1 text-small-caption text-neutral-110">
                      {completedByLabel}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-6 w-6 text-green-150" />
              </button>
            );
          },
        )}
      </div>
    </SlideoverSection>
  );
};

export default HistorySection;
